import { AddAppDialog } from '@/components/shell/add-app-dialog';
import { RevealKeyDialog } from '@/components/shell/reveal-key-dialog';
import { PageHeader } from '@/components/shell/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useContextTree, type CreateAppResult } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowRight, Check, CheckCircle2, Copy, KeyRound, Plus, Rocket, Share2, Smartphone, Sparkles, Terminal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/install')({ component: InstallPage });

// The SDK is not yet published to a public Git URL (v1.0.0 is untagged — see STATUS). This is the one
// placeholder a real external integrator is blocked on; everything else in the flow is live.
const SDK_PACKAGE_URL = 'https://github.com/personalizehq/sdk-ios'; // TODO: publish on the v1.0.0 tag
function keyPlaceholder(environmentName: string | undefined): string {
  const environment = environmentName?.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_') || 'environment';
  return `wk_your_${environment}_key`;
}

function CopyBlock({ code, language }: { code: string; language: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy. Select the snippet and copy it manually.');
    }
  };

  return (
    <div className="group relative overflow-hidden rounded-lg border bg-zinc-950 text-zinc-100 shadow-sm">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{language}</span>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Copy ${language} snippet`}
          className="h-7 px-2 text-zinc-300 hover:bg-white/10 hover:text-white"
          onClick={copy}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="overflow-x-auto p-4 pr-12 font-mono text-xs leading-5">
        <code aria-label={`${language} snippet`}>{code}</code>
      </pre>
    </div>
  );
}

function StepBadge({ number, complete = false }: { number: number; complete?: boolean }): React.JSX.Element {
  return (
    <span
      className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold ${
        complete ? 'bg-emerald-500 text-white' : 'bg-indigo-500/10 text-indigo-600 ring-1 ring-indigo-500/20 dark:text-indigo-300'
      }`}
    >
      {complete ? <Check className="size-4" /> : number}
    </span>
  );
}

export function InstallPage(): React.JSX.Element {
  const { data: orgs, isLoading } = useContextTree();
  const appId = useContextStore((s) => s.appId);
  const environmentId = useContextStore((s) => s.environmentId);

  // The raw production key is only available in the create response — hold it to show once in the
  // reveal dialog, and interpolate it into the snippet while the page is still open.
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [keyForGuide, setKeyForGuide] = useState<{ value: string; environmentId: string } | null>(null);

  const apps = orgs?.flatMap((o) => o.workspaces.flatMap((w) => w.apps)) ?? [];
  const activeApp = apps.find((a) => a.id === appId) ?? apps[0];
  const activeEnvironment = activeApp?.environments.find((environment) => environment.id === environmentId)
    ?? activeApp?.environments[0];
  const hasAnyApp = apps.length > 0;
  const canCreate = !!orgs?.some((o) => o.role === 'admin' || o.role === 'editor');

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Install" description="Connect your app to PersonalizeHQ." />
        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <Skeleton className="h-64 rounded-xl" />
          <Skeleton className="h-[520px] rounded-xl" />
        </div>
      </div>
    );
  }

  const onCreated = (result: CreateAppResult): void => {
    setKeyForGuide({ value: result.write_key, environmentId: result.environment_ids.production });
    setRevealedKey(result.write_key);
  };

  // FIRST RUN — no app yet. This is the guided entry point the home empty state links to.
  if (!hasAnyApp) {
    return (
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader title="Connect your first app" description="Install the SDK and deliver your first in-app experience in a few minutes." />

        <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
          <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
            <div className="relative overflow-hidden border-b p-8 sm:p-10 lg:border-b-0 lg:border-r">
              <div className="absolute -right-16 -top-20 size-56 rounded-full bg-indigo-500/10 blur-3xl" />
              <div className="relative">
                <span className="mb-6 grid size-12 place-items-center rounded-xl bg-indigo-500 text-white shadow-lg shadow-indigo-500/20">
                  <Rocket className="size-5" />
                </span>
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-600 dark:text-indigo-300">Quick start</p>
                <h2 className="mt-2 max-w-lg text-2xl font-semibold tracking-tight sm:text-3xl">Your first experience starts with an app.</h2>
                <p className="mt-3 max-w-lg text-sm leading-6 text-muted-foreground">
                  Give your iOS app a name. PersonalizeHQ will prepare everything you need to build, test, and publish.
                </p>

                {canCreate ? (
                  <AddAppDialog
                    onCreated={onCreated}
                    trigger={
                      <Button size="lg" className="mt-7 px-5">
                        Create iOS app
                        <ArrowRight className="size-4" />
                      </Button>
                    }
                  />
                ) : (
                  <p className="mt-7 rounded-lg border bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
                    Ask an admin or editor on your team to create the first app.
                  </p>
                )}
              </div>
            </div>

            <div className="bg-secondary/20 p-8 sm:p-10">
              <p className="text-sm font-semibold">What we’ll set up</p>
              <div className="mt-6 space-y-5">
                {[
                  { icon: Smartphone, title: 'Your iOS app', body: 'A dedicated home for experiences and campaigns.' },
                  { icon: Sparkles, title: 'Two environments', body: 'Staging for testing and production for customers.' },
                  { icon: KeyRound, title: 'Production SDK key', body: 'Generated once and ready to paste into your app.' },
                ].map(({ icon: Icon, title, body }) => (
                  <div key={title} className="flex gap-3">
                    <span className="grid size-9 shrink-0 place-items-center rounded-lg border bg-background text-muted-foreground shadow-sm">
                      <Icon className="size-4" />
                    </span>
                    <div>
                      <p className="text-sm font-medium">{title}</p>
                      <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-7 border-t pt-5 text-xs leading-5 text-muted-foreground">No credit card required. The iOS SDK has zero third-party dependencies.</p>
            </div>
          </div>
        </div>
        <RevealKeyDialog value={revealedKey} onClose={() => setRevealedKey(null)} title="Copy your SDK key now" />
      </div>
    );
  }

  const visibleKey = keyForGuide && keyForGuide.environmentId === activeEnvironment?.id
    ? keyForGuide.value
    : null;
  const placeholder = keyPlaceholder(activeEnvironment?.name);
  const key = visibleKey ?? placeholder;
  const initSnippet = `import PersonalizeHQ

// In application(_:didFinishLaunchingWithOptions:)
PHQ.start(writeKey: "${key}")`;

  const copySetupGuide = async (): Promise<void> => {
    const guide = `Install PersonalizeHQ in ${activeApp?.name ?? 'our iOS app'} (${activeEnvironment?.name ?? 'selected'} environment)

1. In Xcode, choose File → Add Package Dependencies and add:
${SDK_PACKAGE_URL}

2. Initialize the SDK when the app launches:
${initSnippet}

3. Launch the app once, then create and publish an experience in PersonalizeHQ.`;

    try {
      await navigator.clipboard.writeText(guide);
      toast.success('Setup guide copied');
    } catch {
      toast.error('Could not copy the setup guide');
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title={`Install in ${activeApp?.name ?? 'your app'}`}
        description={`Connect the ${activeEnvironment?.name ?? 'selected'} environment. You can return to this guide at any time.`}
        actions={
          <>
            <Button variant="outline" onClick={copySetupGuide}>
              <Share2 className="size-4" />
              Copy setup guide
            </Button>
            {canCreate ? (
              <AddAppDialog
                onCreated={onCreated}
                trigger={
                  <Button variant="outline">
                    <Plus className="size-4" />
                    Add app
                  </Button>
                }
              />
            ) : null}
          </>
        }
      />

      {visibleKey ? (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
          <CheckCircle2 className="size-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{activeApp?.name ?? 'Your app'} is ready to connect</p>
            <p className="text-xs text-muted-foreground">Your {activeEnvironment?.name} key is already included in the code below.</p>
          </div>
        </div>
      ) : null}

      <div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-xl border bg-secondary/20 p-5 lg:sticky lg:top-6">
          <div className="flex items-center gap-3 border-b pb-4">
            <span className="grid size-9 place-items-center rounded-lg bg-background shadow-sm ring-1 ring-border">
              <Smartphone className="size-4 text-indigo-600 dark:text-indigo-300" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{activeApp?.name ?? 'iOS app'}</p>
              <p className="text-xs capitalize text-muted-foreground">{activeEnvironment?.name ?? 'Selected'} environment</p>
            </div>
          </div>
          <ol className="mt-5 space-y-5" aria-label="Installation steps">
            {[
              { number: 1, title: 'Add the package', body: 'Swift Package Manager' },
              { number: 2, title: 'Initialize the SDK', body: 'One line of configuration' },
              { number: 3, title: 'Create an experience', body: 'Build, preview, and publish' },
            ].map((step) => (
              <li key={step.number} className="flex gap-3">
                <StepBadge number={step.number} />
                <div>
                  <p className="text-sm font-medium">{step.title}</p>
                  <p className="text-xs text-muted-foreground">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="mt-6 border-t pt-4 text-xs leading-5 text-muted-foreground">Usually takes less than 5 minutes.</p>
        </aside>

        <div className="space-y-5">
          <Card className="rounded-xl shadow-sm">
            <CardHeader className="flex-row items-start gap-3 space-y-0">
              <StepBadge number={1} />
              <div>
                <CardTitle className="text-base">Add the SDK</CardTitle>
                <CardDescription className="mt-1">In Xcode, choose File → Add Package Dependencies, then paste this URL.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pl-5 sm:pl-16">
              <CopyBlock code={SDK_PACKAGE_URL} language="Package URL" />
              <p className="text-xs text-muted-foreground">Requires iOS 17 or later. Zero third-party dependencies.</p>
            </CardContent>
          </Card>

          <Card className="rounded-xl shadow-sm">
            <CardHeader className="flex-row items-start gap-3 space-y-0">
              <StepBadge number={2} />
              <div>
                <CardTitle className="text-base">Initialize PersonalizeHQ</CardTitle>
                <CardDescription className="mt-1">Start the SDK when your app launches. The key resolves the app and environment automatically.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pl-5 sm:pl-16">
              <CopyBlock code={initSnippet} language="Swift" />
              {visibleKey ? (
                <p className="flex items-start gap-2 text-xs leading-5 text-amber-700 dark:text-amber-300">
                  <KeyRound className="mt-0.5 size-3.5 shrink-0" />
                  This snippet contains your {activeEnvironment?.name} key. Store it securely; create a replacement in Settings if it is ever lost.
                </p>
              ) : (
                <p className="text-xs leading-5 text-muted-foreground">
                  Replace <code className="rounded bg-secondary px-1 py-0.5 font-mono">{placeholder}</code> with a {activeEnvironment?.name ?? 'selected environment'} key from Settings → Write keys.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-xl border-indigo-500/20 bg-gradient-to-br from-indigo-500/[0.06] to-transparent shadow-sm">
            <CardHeader className="flex-row items-start gap-3 space-y-0">
              <StepBadge number={3} />
              <div>
                <CardTitle className="text-base">Build your first experience</CardTitle>
                <CardDescription className="mt-1">Launch your app once to initialize the SDK, then create and preview an experience before publishing.</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-3 pl-5 sm:flex-row sm:items-center sm:pl-16">
              <Button asChild>
                <Link to="/experiences">
                  <Sparkles className="size-4" />
                  Create an experience
                </Link>
              </Button>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Terminal className="size-3.5" />
                The SDK connects on app launch
              </span>
            </CardContent>
          </Card>
        </div>
      </div>

      <RevealKeyDialog value={revealedKey} onClose={() => setRevealedKey(null)} title="Copy your SDK key now" />
    </div>
  );
}
